<script setup>
import { computed, onMounted, ref } from "vue";

import ModeDetectHelper from "@/Layouts/Partials/ModeDetectHelper.vue";
import ResponsiveHelper from "@/Layouts/Partials/ResponsiveHelper.vue";

import twMerge from "@/tw-merge";
import { Head } from "@inertiajs/vue3";

const emit = defineEmits(["loaded"]);

const props = defineProps({
  title: {
    type: String,
    required: false
  },
  crumbs: {
    type: Array,
    required: false,
    default: () => []
  },
  appClass: {
    type: String,
    required: false
  },
  defaultAppClass: {
    type: String,
    required: false,
    default: ""
  },
  bodyClass: {
    type: [String, Array],
    required: false,
    default: ""
  },
  appStyle: {
    type: Object,
    required: false
  },
  mainClass: {
    type: String,
    required: false
  },
  defaultMainClass: {
    type: String,
    required: false,
    default: "overflow-x-clip"
  },
  mainStyle: {
    type: Object,
    required: false
  },
  screen: {
    type: Boolean,
    required: false
  },
  centered: {
    type: Boolean,
    required: false
  }
});

const isLoaded = ref(false);

const appClasses = computed(() => twMerge(props.defaultAppClass, props.appClass));
const mainClasses = computed(() => twMerge(props.defaultMainClass, props.mainClass));

onMounted(() => {
  if (typeof props.bodyClass === "string" && props.bodyClass.length > 0) {
    document.body.classList.add(...props.bodyClass.split(" "));
  }

  if (Array.isArray(props.bodyClass)) {
    props.bodyClass
      .filter((it) => !!it)
      .forEach((cls) => {
        document.body.classList.add(cls);
      });
  }

  if (navigator.platform.indexOf("Win") > -1) {
    document.body.classList.add("windows");
  }

  document.getElementById("preloader")?.remove();
  isLoaded.value = true;

  emit("loaded", isLoaded.value);
});
</script>

<template>
  <Head :title="title" />
  <!--end::head-->

  <slot name="banner" />
  <!--end::banner-->

  <div id="root" :class="[appClasses, screen ? 'h-screen overflow-hidden' : '']" :style="appStyle">
    <aside v-if="$slots.sidebar" id="sidebar" class="sidebar relative print:hidden">
      <slot name="sidebar" />
    </aside>
    <!--end::sidebar-->

    <div v-if="$slots.drawer">
      <slot name="drawer" />
    </div>
    <!--end::drawer-->

    <slot name="header" :crumbs="crumbs" />
    <!--end::header-->

    <main id="main" :class="[mainClasses, screen ? 'h-screen' : 'min-h-[100vh]']" :style="mainStyle">
      <div class="main-content" :class="[centered ? 'grid place-items-center' : '']">
        <slot />
      </div>
      <!--end::content-->
    </main>
    <!--end::main-->

    <slot name="footer" />
    <!--end::footer-->
  </div>
  <!--end::root-->

  <slot name="navbar" />
  <!--end::mobile-navbar-->

  <div id="v-teleport-target"></div>
  <!--end::teleport-->

  <ResponsiveHelper />
  <ModeDetectHelper />
  <!--end::helper's-->
</template>
